import { StatusDot, Tag, type DotTone, type Tone } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type {
  IntegrationLogDirection,
  IntegrationLogStatus,
} from "@/lib/api/types";

// Declared `Partial` on purpose: the audit log mirrors whatever the providers
// report, so a status/direction the admin has no label for must degrade to the
// raw string instead of pushing `undefined` into `t()` (which crashes on
// `.split`, the bug ThemesPage documents).
const STATUS_TONE: Partial<Record<IntegrationLogStatus, DotTone>> = {
  success: "green",
  failed: "red",
  pending: "amber",
};

const STATUS_LABEL_KEY: Partial<Record<IntegrationLogStatus, string>> = {
  success: keys.integrationLogs.statusSuccess,
  failed: keys.integrationLogs.statusFailed,
  pending: keys.integrationLogs.statusPending,
};

const DIRECTION_TONE: Partial<Record<IntegrationLogDirection, Tone>> = {
  outbound: "blue",
  inbound: "purple",
};

const DIRECTION_LABEL_KEY: Partial<Record<IntegrationLogDirection, string>> = {
  outbound: keys.integrationLogs.directionOutbound,
  inbound: keys.integrationLogs.directionInbound,
};

type StatusChipProps = {
  status: IntegrationLogStatus;
};

export function IntegrationLogStatusChip({ status }: StatusChipProps) {
  const t = useTranslations();
  const labelKey = STATUS_LABEL_KEY[status];

  return (
    // Status is a dot and the word, never a filled pill.
    <StatusDot tone={STATUS_TONE[status] ?? "grey"}>
      {labelKey ? t(labelKey) : status}
    </StatusDot>
  );
}

type DirectionChipProps = {
  direction: IntegrationLogDirection;
};

export function IntegrationLogDirectionChip({ direction }: DirectionChipProps) {
  const t = useTranslations();
  const labelKey = DIRECTION_LABEL_KEY[direction];

  return (
    <Tag tone={DIRECTION_TONE[direction] ?? "grey"}>
      {labelKey ? t(labelKey) : direction}
    </Tag>
  );
}
